import type { AbilityDemo } from '@/features/playbook/types';

const demo: AbilityDemo = {
  id: 'profession',
  slug: 'profession-scribe',
  label: 'Profession (Scribe)',
  roll: {
    formula: 'Com 2 + Scribe 2 = 4 + die',
    niche: '+1 when producing written work',
    altPairing: 'Dex -1 for ornate penmanship only (and that total will hurt)',
  },
  whenToUse: [
    'Copying a manuscript faithfully without introducing errors or omissions.',
    'Drafting a legally sound contract or covenant document under pressure.',
    'Maintaining ledgers and records so they remain legible and organized.',
    'Producing a letter with clarity of prose that persuades or impresses its reader.',
  ],
  samples: [
    `GM: "Ex Astra scouting, spring 1195. You follow in Elara's wake as the party's scribe, documenting the day's route, landmarks, and observations in your field journal."
Elara: "Present me a clean entry by day's end for my seal."
You (Mikaela): "I render each line carefully, checking my script twice. My Perfectionist drives me to re-check every entry—the covenant's records depend on precision."
GM: "Roll Com 2 + Scribe 2 = 4 + die."

Teaches: Perfectionist +2 drives re-checks—your total 4 turns daily notes into formal covenant records.`,
    `GM: "Ex Astra spring 1195. High in a cliff face, a half-effaced Roman inscription in weathered capitals. Elara needs a precise copy to study later."
Corvus: "Can you make out the letters?"
You (Mikaela): "I'll squint in this afternoon light and transcribe each character onto fresh parchment. My hand must not add ambiguity where the stone already fades. I'm just a careful scribe, not an artist."
GM: "Roll Com 2 + Scribe 2 = 4 + die."

Teaches: Humble flaw makes you understate prose quality—but niche Writing = 5 elevates fair copy into craft.`,
    `GM: "A messenger arrives at camp. The Primus must be informed of progress and the route ahead. Sir Helgi composes a brief report. [Ex Astra scouting, spring 1195, "Not-Quite-Free Real Estate" sidequest.]"
Brother Alfric: "The rider leaves at dawn."
You (Mikaela): "I draft the letter cleanly onto travel parchment, each character formed clearly so no rider carries ambiguous words. I copy it once, check it twice, then offer it to Sir Helgi. My Perfectionist won't let me seal it otherwise."
GM: "Roll Com 2 + Scribe 2 = 4 + die."

Teaches: Perfectionist +2 drives re-checks—total 4 drafts missives under pressure; Writing = 5.`,
    `GM: "The sidequest deepens. Three days to gather rare ingredients. The party returns with specimens, pouches, bundles. Everything must be catalogued before departure. Ex Astra spring 1195."
Elara: "We leave in two hours. I need weights and counts."
You (Mikaela): "I compile the ledger line by line—ingredient, quantity, condition. The herbalist must decipher this weeks hence. I check each entry against the actual pouches. My Clerk virtue grants me scribe access to covenant records."
GM: "Roll Com 2 + Scribe 2 = 4 + die."

Teaches: Clerk virtue grants scribe access—your total 4 transforms a sidequest into an inventory the covenant trusts.`,
    `GM: "A local lord grants Ex Astra safe passage through his lands in spring 1195. He provides a hand-written grant on rough parchment, smudged and informal. The covenant will need an official record."
Sir Helgi: "Make this presentable."
You (Mikaela): "I transcribe the grant onto formal vellum with careful script, rendering his terms into a document fit for nobles or the Primus. I re-read it three times to ensure no misreading alters his intent. My Perfectionist compels this."
GM: "Roll Com 2 + Scribe 2 = 4 + die."

Teaches: Perfectionist +2 checks and re-checks—total 4 lifts fair copy into an act that binds lord and party.`,
  ],
  vignette: "Your fingers ache after days bent over the scriptorium desk, but the copy is flawless. The magus trusts you with the covenant's most sensitive records—not because you have virtue or power, but because your hand will not betray the words.",
  gotcha: 'Scribe is the craft (what you output). Concentration is the focus to produce it. Artes Liberales is the theory of language. Roll Scribe for "did the copy come out clean?".',
};

export default demo;
